import { useState } from "react";
import { AlertTriangle, CheckCircle2, Copy, Database, Loader2, Search, Send, Unlock } from "lucide-react";
import { toast } from "sonner";
import { foundationSyncCheck, getFoundationPOReadback, poUpdatedInFoundation } from "@/lib/api";
import { FoundationDryRunButton, ReportSyncError } from "./FoundationDryRunButton";
import { FoundationPOReadbackDialog } from "./FoundationPOReadbackDialog";
import { COUnlockDialog, useCanUnlockSync } from "./COUnlockDialog";

const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
const fmt = (s) => (s ? String(s).slice(0, 16).replace("T", " ") : "—");
const KIND = { purchase_order: { short: "PO", cls: "bg-violet-100 text-violet-800" }, change_order: { short: "CO", cls: "bg-indigo-100 text-indigo-800" } };
const SYNC_CLS = { ready: "bg-sky-100 text-sky-800", failed: "bg-rose-100 text-rose-800", pushed: "bg-blue-100 text-blue-800", verified: "bg-emerald-100 text-emerald-800" };
export const rowId = (row) => `${KIND[row.kind].short}-${row.wo_id}-${row.doc_id}`;
const ERROR_TITLE = { blocked: "Blocked before any INSERT", sql_rejected: "Rejected by SQL Server", unreachable: "Could not reach Foundation", deleted_in_screen: "Deleted in Foundation's sync screen" };

const copyText = async (text) => {
  try { await navigator.clipboard.writeText(text); toast.success("Copied"); }
  catch { window.prompt("Copy the error text:", text); }
};

function RowHead({ row }) {
  const k = KIND[row.kind];
  const id = rowId(row);
  return (
    <>
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${k.cls}`} data-testid={`fsq-kind-${id}`}>{k.short}</span>
        <span className="font-mono text-sm font-semibold text-slate-800 dark:text-slate-100" data-testid={`fsq-number-${id}`}>{row.number}</span>
        {row.foundation_no && row.foundation_no !== row.number && <span className="font-mono text-[10px] text-slate-500 dark:text-slate-300">→ Foundation {row.foundation_no}</span>}
        <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${SYNC_CLS[row.sync_status] || "bg-slate-100 text-slate-600"}`} title={row.sync_message || ""} data-testid={`fsq-status-${id}`}>
          {row.sync_label || row.sync_status}{row.attempts ? ` · ${row.attempts}×` : ""}
        </span>
        {row.is_test && <span className="text-[9px] font-semibold text-violet-700 bg-violet-50 border border-violet-200 rounded px-1">TEST JOB</span>}
      </div>
      <div className="text-[11px] text-slate-600 dark:text-slate-300 mt-0.5" data-testid={`fsq-meta-${id}`}>
        WO <span className="font-mono">{row.wo_number || "—"}</span>{row.wo_title ? ` · ${row.wo_title}` : ""}{row.customer ? ` · ${row.customer}` : ""}
        {row.party ? ` · ${row.party}` : ""} · <span className="tabular-nums">{money(row.total)}</span>
        {row.approved_at ? ` · approved ${fmt(row.approved_at)}` : ""}{row.pushed_at ? ` · inserted ${fmt(row.pushed_at)}` : ""}
      </div>
    </>
  );
}

function ErrorBlock({ row }) {
  const text = row.last_error || row.sync_message || "";
  const title = ERROR_TITLE[row.error_kind] || "Failed";
  const id = rowId(row);
  return (
    <div className="mt-1.5 rounded border border-rose-200 bg-rose-50 dark:bg-rose-950/40 dark:border-rose-800 p-2 text-[11px] text-rose-900 dark:text-rose-100" data-testid={`fsq-error-${id}`}>
      <div className="flex items-center gap-1.5 font-semibold">
        <AlertTriangle className="w-3.5 h-3.5 shrink-0" /> {title}
        {row.error_kind && <span className="font-mono text-[10px] px-1 rounded bg-rose-100 dark:bg-rose-900 border border-rose-200 dark:border-rose-700" data-testid={`fsq-error-kind-${id}`}>{row.error_kind}</span>}
        <span className="opacity-70 font-normal">{fmt(row.updated_at)}</span>
        <button type="button" onClick={() => copyText(`${row.number}\n${text}${row.sql ? `\n\n${row.sql}` : ""}`)} className="ml-auto inline-flex items-center gap-0.5 hover:underline" data-testid={`fsq-copy-error-${id}`}>
          <Copy className="w-3 h-3" /> Copy
        </button>
      </div>
      {row.stale_error && (
        <div className="mt-1 text-amber-800 dark:text-amber-200 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 rounded px-1.5 py-0.5" data-testid={`fsq-stale-error-${id}`}>
          Outdated — this block came from an older bridge version; the bridge is updated now. Click Send again.
        </div>
      )}
      <pre className="mt-1 whitespace-pre-wrap font-mono text-[10px] break-words" data-testid={`fsq-error-text-${id}`}>{text}</pre>
      {row.sql && <details className="mt-1"><summary className="cursor-pointer">SQL</summary><pre className="whitespace-pre-wrap font-mono text-[10px]">{row.sql}</pre></details>}
    </div>
  );
}

export function FoundationSyncRow({ row, busy, disabled, current, onSend, onChanged }) {
  const failed = row.sync_status === "failed";
  const id = rowId(row);
  const tid = `fsq-preview-${id}`;
  return (
    <div className={`rounded-md border bg-white dark:bg-slate-900 p-2.5 ${current ? "border-sky-400 ring-1 ring-sky-200" : failed ? "border-rose-200 dark:border-rose-800" : "border-slate-200 dark:border-slate-700"}`} data-testid={`fsq-row-${id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <RowHead row={row} />
          {failed && <ErrorBlock row={row} />}
          {!failed && row.error_kind === "unreachable" && row.sync_message && (
            <div className="mt-1 text-[11px] text-amber-800" data-testid={`fsq-note-${id}`}>{row.sync_message}</div>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap shrink-0">
          <button type="button" onClick={onSend} disabled={busy || disabled}
            className={`text-xs px-3 py-1.5 rounded text-white disabled:opacity-50 inline-flex items-center gap-1 ${failed ? "bg-rose-600 hover:bg-rose-700" : "bg-emerald-600 hover:bg-emerald-700"}`}
            title="Dry run first (rolled back), then the real insert of this one document" data-testid={`fsq-send-${id}`}>
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />} {failed ? "Send again" : "Send to Foundation"}
          </button>
          <FoundationDryRunButton woId={row.wo_id} kind={row.kind} docId={row.doc_id} testId={tid} only="preview" onPushed={onChanged} />
        </div>
      </div>
    </div>
  );
}

export function FoundationStagedRow({ row, onChanged, stagingClear = true }) {
  const id = rowId(row);
  const imported = row.sync_status === "verified";
  const canUnlock = useCanUnlockSync();
  const [checking, setChecking] = useState(false);
  const [reading, setReading] = useState(false);
  const [readback, setReadback] = useState(null);
  const [report, setReport] = useState(false);
  const [unlock, setUnlock] = useState(false);
  const check = async () => {
    setChecking(true);
    try {
      const { data } = await foundationSyncCheck(row.wo_id, row.kind, row.doc_id);
      toast[data.status === "verified" ? "success" : data.status === "failed" ? "error" : "info"](`${row.number}: ${data.message || data.label || data.status}`);
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Check failed"); }
    finally { setChecking(false); }
  };
  const runReadback = async () => {
    setReading(true);
    try { const { data } = await getFoundationPOReadback(row.wo_number); setReadback(data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Readback failed"); }
    finally { setReading(false); }
  };
  const tone = imported ? "border-emerald-200 dark:border-emerald-900 bg-emerald-50/40" : "border-blue-200 dark:border-blue-900 bg-blue-50/40";
  return (
    <div className={`rounded-md border ${tone} dark:bg-slate-900 p-2.5`} data-testid={`fsq-staged-${id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <RowHead row={row} />
          {row.sync_message && <div className="text-[11px] text-blue-900 dark:text-blue-200 mt-0.5">{row.sync_message}</div>}
          {row.screen_error && (
            <div className="mt-1 text-[11px] text-rose-800 bg-rose-50 border border-rose-200 rounded px-2 py-1" data-testid={`fsq-screen-error-${id}`}>
              Foundation screen ({fmt(row.screen_error_at)}): {row.screen_error}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap shrink-0 text-[11px]">
          {!imported && (
            <button type="button" onClick={check} disabled={checking} className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-blue-300 text-blue-800 hover:bg-blue-50 disabled:opacity-50" data-testid={`fsq-check-${id}`}>
              {checking ? <Loader2 className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3" />} Check Foundation
            </button>
          )}
          {row.kind === "purchase_order" && row.wo_number && (
            <button type="button" onClick={runReadback} disabled={reading} className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-slate-300 hover:bg-slate-100 disabled:opacity-50" data-testid={`fsq-readback-${id}`}>
              {reading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Database className="w-3 h-3" />} Readback
            </button>
          )}
          {row.kind === "change_order" && canUnlock && (
            <button type="button" onClick={() => setUnlock(true)}
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border ${stagingClear ? "border-amber-300 text-amber-800 hover:bg-amber-50" : "border-slate-300 text-slate-500"}`}
              title={stagingClear ? "Purged in Foundation? Unlock it here to revise and re-send under the same number" : "Needs bridge 2.6.2 (staging-clear) — IT: Check for bridge update"}
              data-testid={`fsq-unlock-${id}`}>
              <Unlock className="w-3 h-3" /> Unlock to revise
            </button>
          )}
          <button type="button" onClick={() => setReport(true)} className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-rose-300 text-rose-700 hover:bg-rose-50" data-testid={`fsq-report-${id}`}>
            <AlertTriangle className="w-3 h-3" /> Report sync error
          </button>
        </div>
      </div>
      {report && <ReportSyncError woId={row.wo_id} kind={row.kind} docId={row.doc_id} onClose={() => { setReport(false); onChanged?.(); }} />}
      {readback && <FoundationPOReadbackDialog row={row} data={readback} onClose={() => setReadback(null)} />}
      {unlock && (
        <COUnlockDialog woId={row.wo_id} woNumber={row.wo_number}
          co={{ id: row.doc_id, co_number: row.number, foundation_sync: { status: row.sync_status, label: row.sync_label, foundation_change_order_no: row.foundation_no } }}
          onClose={() => setUnlock(false)} onUnlocked={() => onChanged?.()} />
      )}
    </div>
  );
}

// A PO edited after it was staged / imported — accounting keys the change into Foundation by hand, then clears the flag.
export function FoundationChangedRow({ row, onChanged }) {
  const id = rowId(row);
  const [busy, setBusy] = useState(false);
  const ch = row.changed_after_sync || {};
  const deltas = ch.deltas || [];
  const clear = async () => {
    setBusy(true);
    try {
      const { data } = await poUpdatedInFoundation(row.wo_id, row.doc_id);
      toast.success(data?.message || `${row.number}: flag cleared`);
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not clear the flag"); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-md border border-amber-300 bg-amber-50/60 dark:bg-slate-900 dark:border-amber-800 p-2.5" data-testid={`fsq-changed-${id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <RowHead row={row} />
          <div className="mt-1 text-[11px] text-amber-900 dark:text-amber-100 flex items-center gap-1 font-semibold">
            <AlertTriangle className="w-3.5 h-3.5 shrink-0" /> Changed since sent{ch.by ? ` by ${ch.by}` : ""}{ch.at ? ` · ${fmt(ch.at)}` : ""} — Foundation still has the original
          </div>
          <ul className="mt-1 space-y-0.5 text-[11px] text-slate-700 dark:text-slate-200" data-testid={`fsq-changed-deltas-${id}`}>
            {deltas.map((d, i) => (
              <li key={i} className="flex items-baseline gap-1.5 flex-wrap" data-testid={`fsq-changed-delta-${id}-${i}`}>
                <span className="font-medium">{d.label}:</span>
                <span className="font-mono line-through opacity-70">{d.from ?? "—"}</span>
                <span>→</span>
                <span className="font-mono">{d.to ?? "—"}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="flex items-center gap-2 flex-wrap shrink-0 text-[11px]">
          <button type="button" onClick={clear} disabled={busy}
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50"
            title="I keyed these changes into Foundation by hand — clear the flag" data-testid={`fsq-updated-in-foundation-${id}`}>
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />} Updated in Foundation
          </button>
        </div>
      </div>
    </div>
  );
}
